import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  CalendarRange,
  CalendarDays,
  PenLine,
  ClipboardList,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Users,
  Layers,
  Clock,
  Eye,
  Hourglass,
  Sparkles,
} from 'lucide-react'
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  format,
  isToday,
  isSameWeek,
} from 'date-fns'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import {
  useGetWPPlansQuery,
  useLazyGetWPPreviousWeekReviewQuery,
  WPPlanRow,
} from '@/redux/service/weeklyPlanning'
import { useGetTeamsQuery } from '@/redux/service/employee'
import WeeklyResourcePlanningDashboard from '@/pages/WeeklyResourcePlanningDashboard'
import WeeklyPlanningStudio from '@/components/studio/WeeklyPlanningStudio'
import PlanDialog, { DialogMode } from '@/components/weeklyPlanning/PlanDialog'
import PreviousWeekReviewDialog from '@/components/weeklyPlanning/PreviousWeekReviewDialog'
import WeeklyViewModel from '@/components/weeklyPlanning/WeeklyViewModel'
import { IS_PRODUCTION } from '@/constant'
function getMonday(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 })
}

function formatWeekLabel(monday: Date): string {
  const sunday = endOfWeek(monday, { weekStartsOn: 1 })
  if (monday.getMonth() === sunday.getMonth()) {
    return `${format(monday, 'd')} – ${format(sunday, 'd MMM yyyy')}`
  }
  return `${format(monday, 'd MMM')} – ${format(sunday, 'd MMM yyyy')}`
}

function formatDateStr(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

const planStatusConfig = {
  no_plan: {
    label: 'No Plan',
    className: 'bg-muted text-muted-foreground border-border',
  },
  draft: {
    label: 'Draft',
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  },
  submitted: {
    label: 'Submitted',
    className: 'bg-green-500/10 text-green-600 border-green-500/20',
  },
}

type DialogState = {
  isOpen: boolean
  mode: DialogMode
  member: WPPlanRow | null
}

export default function WeeklyResourcePlanning() {
  const { isTeamLeader, canEditSubmittedWP } = useCheckPermission()

  const [currentMonday, setCurrentMonday] = useState<Date>(() =>
    getMonday(new Date())
  )
  const [calOpen, setCalOpen] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<string>('all')
  const [dialog, setDialog] = useState<DialogState>({
    isOpen: false,
    mode: 'plan',
    member: null,
  })
  const [activeTab, setActiveTab] = useState<'list' | 'dashboard' | 'studio'>(
    'list'
  )
  const [reviewMember, setReviewMember] = useState<WPPlanRow | null>(null)
  const [checkingMemberId, setCheckingMemberId] = useState<number | null>(null)
  const [fetchPreviousWeekReview] = useLazyGetWPPreviousWeekReviewQuery()
  const [viewPlanId, setViewPlanId] = useState<number | null>(null)

  const weekStartStr = formatDateStr(currentMonday)
  const isCurrentWeek = isSameWeek(currentMonday, new Date(), {
    weekStartsOn: 1,
  })

  const { data: teamsData } = useGetTeamsQuery()

  const teamIdParam =
    !isTeamLeader && selectedTeam !== 'all' ? Number(selectedTeam) : undefined

  const plansQueryArg =
    teamIdParam !== undefined
      ? { week_start: weekStartStr, team_id: teamIdParam }
      : { week_start: weekStartStr }

  const {
    data: plansData,
    isLoading,
    isFetching,
  } = useGetWPPlansQuery(plansQueryArg)

  const rows = useMemo(
    () => (plansData?.success ? (plansData.data ?? []) : []),
    [plansData]
  )

  const openPlanDialogForMember = (member: WPPlanRow) => {
    setDialog({ isOpen: true, mode: 'plan', member })
  }

  const openDialog = async (member: WPPlanRow, mode: DialogMode) => {
    if (mode !== 'plan') {
      setDialog({ isOpen: true, mode, member })
      return
    }
    setCheckingMemberId(member.user_id)
    try {
      const res = await fetchPreviousWeekReview({
        member_user_id: member.user_id,
        week_start: weekStartStr,
      }).unwrap()
      if (res.data.acknowledgement_required) {
        setReviewMember(member)
      } else {
        openPlanDialogForMember(member)
      }
    } catch {
      openPlanDialogForMember(member)
    } finally {
      setCheckingMemberId(null)
    }
  }

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }))
  }

  const handleReviewAcknowledged = () => {
    const m = reviewMember
    setReviewMember(null)
    if (m) openPlanDialogForMember(m)
  }

  const handleAckRequiredFromPlanDialog = () => {
    const m = dialog.member
    closeDialog()
    if (m) setReviewMember(m)
  }

  const summaryStats = useMemo(() => {
    const total = rows.length
    const submitted = rows.filter(r => r.plan_status === 'submitted').length
    const draft = rows.filter(r => r.plan_status === 'draft').length
    const noPlan = rows.filter(r => r.plan_status === 'no_plan').length

    const totalBillable = rows.reduce(
      (acc, r) => acc + (Number.parseFloat(r.est_billing_hrs) || 0),
      0
    )
    const totalNonBillable = rows.reduce(
      (acc, r) => acc + (Number.parseFloat(r.est_non_billing_hrs) || 0),
      0
    )

    return {
      total,
      submitted,
      draft,
      noPlan,
      totalBillable,
      totalNonBillable,
    }
  }, [rows])

  return (
    <div className='h-full flex flex-col space-y-4 bg-background text-foreground selection:bg-primary/30 relative overflow-hidden'>
      {/* Background blobs for depth */}
      {/* <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute -top-[5%] -left-[5%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]' />
        <div className='absolute bottom-[10%] -right-[5%] w-[35%] h-[35%] bg-blue-500/5 rounded-full blur-[100px]' />
      </div> */}
      <div className='shrink-0 flex flex-col md:flex-row md:items-end justify-between gap-8 relative z-10'>
        <div className='space-y-3'>
          <div className='flex items-center gap-4'>
            <div className='p-4 rounded-[22px] bg-primary text-primary-foreground shadow-xl '>
              <CalendarRange size={24} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className='text-2xl font-black tracking-tight text-foreground'>
                Weekly Planning
              </h1>
              <p className=' font-bold text-[12px]  text-muted-foreground/60 uppercase mt-1'>
                Strategic allocation & efficiency tracking
              </p>
            </div>
          </div>
        </div>

        <div className='flex p-1.5 bg-muted/50 backdrop-blur-xl border border-border/50 rounded-2xl gap-1 shrink-0 w-fit items-stretch shadow-sm'>
          <button
            onClick={() => setActiveTab('list')}
            className={`px-6 py-2.5 text-[11px] font-black uppercase tracking-widest transition-all duration-300 rounded-xl flex items-center gap-2 ${
              activeTab === 'list'
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            }`}
          >
            Detailed List
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-2.5 text-[11px] font-black uppercase tracking-widest transition-all duration-300 rounded-xl flex items-center gap-2 ${
              activeTab === 'dashboard'
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            }`}
          >
            Insights
          </button>
          {IS_PRODUCTION && (
            <button
              onClick={() => setActiveTab('studio')}
              className={`px-6 py-2.5 text-[11px] font-black uppercase tracking-widest transition-all duration-300 rounded-xl flex items-center gap-2 ${
                activeTab === 'studio'
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              <Sparkles size={14} />
              Studio
            </button>
          )}
        </div>
      </div>

      <AnimatePresence mode='wait'>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className='flex-1 flex flex-col min-h-0 relative z-10 overflow-hidden'
        >
          {activeTab === 'dashboard' ? (
            <WeeklyResourcePlanningDashboard />
          ) : activeTab === 'studio' ? (
            <WeeklyPlanningStudio />
          ) : (
            <div className='flex-1 flex flex-col min-h-0 space-y-8 overflow-hidden'>
              <div className='shrink-0 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-6 bg-card/40 backdrop-blur-2xl p-2 rounded-lg  border border-border/50 shadow-sm'>
                <div className='flex items-center gap-2 bg-muted/50 p-1.5 rounded-[20px] border border-border/40 w-fit'>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-10 w-10 rounded-2xl hover:bg-background shadow-sm transition-all'
                    onClick={() =>
                      setCurrentMonday(prev => getMonday(subWeeks(prev, 1)))
                    }
                  >
                    <ChevronLeft size={18} strokeWidth={2.5} />
                  </Button>

                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button className='flex items-center justify-center gap-2.5 px-5 py-2 text-[13px] font-black text-foreground hover:bg-background rounded-2xl transition-all duration-300 min-w-0'>
                        <CalendarDays size={16} className='text-primary' />
                        <span className='truncate tracking-tight'>
                          {formatWeekLabel(currentMonday)}
                        </span>
                        <ChevronDown
                          size={14}
                          className='text-muted-foreground/60'
                        />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className='w-auto p-0 bg-background/95 backdrop-blur-3xl border-border shadow-2xl rounded-[32px] overflow-hidden z-[100]'
                      align='start'
                    >
                      <div className='p-2'>
                        <Calendar
                          mode='single'
                          selected={currentMonday}
                          onSelect={d => {
                            if (d) {
                              setCurrentMonday(getMonday(d))
                              setCalOpen(false)
                            }
                          }}
                          modifiers={{
                            weekHighlight: d =>
                              isSameWeek(d, currentMonday, { weekStartsOn: 1 }),
                          }}
                          modifiersClassNames={{
                            weekHighlight:
                              '!bg-primary/20 !text-primary !rounded-none first:!rounded-l-xl last:!rounded-r-xl font-black',
                          }}
                          initialFocus
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-10 w-10 rounded-2xl hover:bg-background shadow-sm transition-all'
                    onClick={() =>
                      setCurrentMonday(prev => getMonday(addWeeks(prev, 1)))
                    }
                  >
                    <ChevronRight size={18} strokeWidth={2.5} />
                  </Button>

                  {!isCurrentWeek && (
                    <Button
                      variant='ghost'
                      className='h-10 px-5 text-[11px] font-black uppercase tracking-widest text-primary hover:bg-primary/10 rounded-2xl border-l border-border/40 ml-1 shadow-none'
                      onClick={() => setCurrentMonday(getMonday(new Date()))}
                    >
                      Return
                    </Button>
                  )}
                </div>

                {!isTeamLeader && (
                  <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                    <SelectTrigger className='h-12 w-full lg:w-56 rounded-[22px] border-border/50 bg-muted/30 font-black text-[13px] hover:bg-muted/50 transition-all px-6'>
                      <div className='flex items-center gap-3'>
                        <Users size={16} className='text-primary' />
                        <SelectValue placeholder='All Teams' />
                      </div>
                    </SelectTrigger>
                    <SelectContent className='rounded-[24px] border-border/50 shadow-2xl p-2'>
                      <SelectItem value='all' className='rounded-xl font-bold'>
                        All Departments
                      </SelectItem>
                      {teamsData?.data?.map(t => (
                        <SelectItem
                          key={t.id}
                          value={String(t.id)}
                          className='rounded-xl font-bold'
                        >
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {!isLoading && rows.length > 0 && (
                <div className='shrink-0 flex items-center gap-4 flex-wrap px-1'>
                  <div className='px-5 py-2 rounded-full bg-accent/30 border border-border/50 text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2'>
                    <Users size={12} className='text-primary' />
                    {summaryStats.total} Member
                  </div>
                  <div className='px-5 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-[10px] font-black uppercase tracking-widest text-green-600 flex items-center gap-2'>
                    <CheckCircle2 size={12} strokeWidth={2.5} />
                    {summaryStats.submitted} submitted
                  </div>
                  <div className='px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest text-amber-600 flex items-center gap-2'>
                    <PenLine size={12} strokeWidth={2.5} />
                    {summaryStats.draft} Drafts
                  </div>
                  <div className='px-5 py-2 rounded-full bg-primary/10 border border-primary/20 text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2'>
                    <ClipboardList size={12} strokeWidth={2.5} />
                    {summaryStats.totalBillable.toFixed(1)} Billable Hrs
                  </div>
                  <div className='px-5 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-[10px] font-black uppercase tracking-widest text-blue-600 flex items-center gap-2'>
                    <Clock size={12} strokeWidth={2.5} />
                    {summaryStats.totalNonBillable.toFixed(1)} Non-Billable Hrs
                  </div>
                  <div className='px-5 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-[10px] font-black uppercase tracking-widest text-green-600 flex items-center gap-2'>
                    <Hourglass size={12} strokeWidth={2.5} />
                    {(
                      summaryStats.totalBillable + summaryStats.totalNonBillable
                    ).toFixed(1)}{' '}
                    Total Planned Hrs
                  </div>
                </div>
              )}

              <div className='flex-1 rounded-xl border border-border/50 bg-card/30 backdrop-blur-2xl shadow-xl overflow-auto ring-1 ring-border/50'>
                <Table className='border-none'>
                  <TableHeader className='bg-accent/10'>
                    <TableRow className='border-b border-border/50 h-16'>
                      <TableHead className='pl-10 truncate'>Member</TableHead>
                      {!isTeamLeader && (
                        <TableHead className='truncate'>Team</TableHead>
                      )}
                      <TableHead className='text-right truncate'>
                        Est. Billable
                      </TableHead>
                      <TableHead className='text-right hidden lg:table-cell truncate'>
                        Non-Billable
                      </TableHead>
                      <TableHead className='text-right hidden lg:table-cell truncate'>
                        Total-hours
                      </TableHead>
                      <TableHead className='text-right hidden lg:table-cell truncate'>
                        Achieved Billable
                      </TableHead>
                      <TableHead className='hidden md:table-cell truncate'>
                        Bench Alert
                      </TableHead>
                      <TableHead className='truncate'>Status</TableHead>
                      <TableHead className='text-right pr-10 truncate'>
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading || isFetching ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <TableRow
                          key={i}
                          className='border-b border-border/20 h-20'
                        >
                          <TableCell className='pl-10'>
                            <Skeleton className='h-5 w-40 rounded-xl' />
                          </TableCell>
                          {!isTeamLeader && (
                            <TableCell>
                              <Skeleton className='h-5 w-24 rounded-xl' />
                            </TableCell>
                          )}
                          <TableCell>
                            <Skeleton className='h-5 w-16 ml-auto rounded-xl' />
                          </TableCell>
                          <TableCell className='hidden lg:table-cell'>
                            <Skeleton className='h-5 w-16 ml-auto rounded-xl' />
                          </TableCell>
                          <TableCell className='hidden lg:table-cell'>
                            <Skeleton className='h-5 w-16 ml-auto rounded-xl' />
                          </TableCell>
                          <TableCell className='hidden md:table-cell'>
                            <Skeleton className='h-5 w-28 rounded-xl' />
                          </TableCell>
                          <TableCell>
                            <Skeleton className='h-7 w-24 rounded-full' />
                          </TableCell>
                          <TableCell className='pr-10'>
                            <Skeleton className='h-10 w-32 ml-auto rounded-2xl' />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : rows.length > 0 ? (
                      rows.map((row, idx) => {
                        const statusCfg =
                          planStatusConfig[row.plan_status] ??
                          planStatusConfig.no_plan
                        const benchDate = row.est_bench_date
                          ? new Date(row.est_bench_date)
                          : null

                        return (
                          <motion.tr
                            key={row.user_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className='group border-b border-border/20 last:border-0 hover:bg-primary/[0.03] transition-colors h-20'
                          >
                            <TableCell className='pl-10'>
                              <div className='flex flex-col'>
                                <span className='font-bold  text-foreground tracking-tight truncate capitalize'>
                                  {row.member_name}
                                </span>
                              </div>
                            </TableCell>
                            {!isTeamLeader && (
                              <TableCell>
                                <span className='text-xs font-bold text-muted-foreground truncate uppercase  bg-accent/20 px-3 py-1 rounded-sm border border-border/30'>
                                  {row.team ?? '—'}
                                </span>
                              </TableCell>
                            )}
                            <TableCell className='text-right truncate'>
                              <span className='font-semibold text-[14px] text-foreground'>
                                {row.plan_status === 'no_plan'
                                  ? '—'
                                  : `${parseFloat(row.est_billing_hrs).toFixed(1)}h`}
                              </span>
                            </TableCell>
                            <TableCell className='text-right text-muted-foreground hidden lg:table-cell truncate'>
                              <span className='font-semibold text-[13px]'>
                                {row.plan_status === 'no_plan'
                                  ? '—'
                                  : `${parseFloat(row.est_non_billing_hrs).toFixed(1)}h`}
                              </span>
                            </TableCell>
                            <TableCell className='text-right text-muted-foreground hidden lg:table-cell truncate'>
                              <span className='font-bold text-[13px]'>
                                {row.plan_status === 'no_plan'
                                  ? '—'
                                  : `${(
                                      Number(row.est_billing_hrs || 0) +
                                      Number(row.est_non_billing_hrs || 0)
                                    ).toFixed(1)}h`}
                              </span>
                            </TableCell>
                            <TableCell className='text-right hidden lg:table-cell truncate'>
                              <span className='font-semibold text-[14px] '>
                                {row.plan_status === 'no_plan'
                                  ? '—'
                                  : `${parseFloat(row.achieved_billable_hrs).toFixed(1)}h`}
                              </span>
                            </TableCell>
                            <TableCell className='hidden md:table-cell text-center truncate'>
                              {benchDate ? (
                                <div
                                  className={cn(
                                    'inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border font-semibold text-[11px] shadow-sm',
                                    isToday(benchDate)
                                      ? 'text-rose-500 bg-rose-500/10 border-rose-500/20 animate-pulse'
                                      : 'text-muted-foreground bg-accent/20 border-border/40'
                                  )}
                                >
                                  <Clock size={12} strokeWidth={3} />
                                  {format(benchDate, 'dd MMM')}
                                </div>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <span
                                className={cn(
                                  'inline-flex items-center px-4 py-1.5 rounded-sm text-[10px] font-semibold uppercase tracking-[0.1em] border shadow-sm truncate',
                                  statusCfg.className
                                )}
                              >
                                {statusCfg.label}
                              </span>
                            </TableCell>
                            <TableCell className='text-right pr-10 truncate'>
                              <div className='flex items-center justify-end gap-3  transition-all duration-300 translate-x-2 group-hover:translate-x-0'>
                                {row.plan_status === 'no_plan' && (
                                  <Button
                                    variant='outline'
                                    onClick={() => openDialog(row, 'plan')}
                                    className='h-11 rounded-sm border-border/50 bg-background hover:bg-primary hover:text-primary-foreground font-black text-[11px] uppercase tracking-widest shadow-lg shadow-black/5 transition-all active:scale-95'
                                  >
                                    {checkingMemberId === row.user_id ? (
                                      <Loader2 className='h-4 w-4 animate-spin' />
                                    ) : (
                                      <ClipboardList
                                        size={16}
                                        strokeWidth={2.5}
                                        className='mr-2'
                                      />
                                    )}
                                    Initialize
                                  </Button>
                                )}
                                {row.plan_status === 'draft' && (
                                  <>
                                    <Button
                                      variant='outline'
                                      onClick={() => openDialog(row, 'edit')}
                                      className='h-11 rounded-sm border-border/50 bg-background hover:bg-accent  text-[11px] uppercase tracking-widest shadow-lg shadow-black/5 transition-all active:scale-95'
                                    >
                                      <PenLine
                                        size={16}
                                        strokeWidth={2.5}
                                        className='mr-2'
                                      />
                                      Modify
                                    </Button>

                                    <Button
                                      onClick={() => openDialog(row, 'submit')}
                                      className='h-11 rounded-2xl bg-primary text-primary-foreground font-black text-[11px] uppercase tracking-widest shadow-xl shadow-primary/20 transition-all active:scale-95'
                                    >
                                      <CheckCircle2
                                        size={16}
                                        strokeWidth={2.5}
                                        className='mr-2'
                                      />
                                      Finalize
                                    </Button>
                                  </>
                                )}
                                {row.plan_status === 'submitted' && (
                                  <>
                                    <Button
                                      variant='outline'
                                      size='sm'
                                      onClick={() => setViewPlanId(row.plan_id)}
                                      title='View'
                                      className='h-11 rounded-2xl border-border/50 bg-background hover:bg-accent font-black text-[11px] uppercase tracking-widest shadow-lg shadow-black/5 transition-all active:scale-95'
                                    >
                                      <Eye
                                        size={16}
                                        strokeWidth={2.5}
                                        className='mr-2'
                                      />
                                      <span>View</span>
                                    </Button>
                                    {canEditSubmittedWP && (
                                      <Button
                                        variant='outline'
                                        onClick={() => openDialog(row, 'edit')}
                                        className='h-11 rounded-2xl border-border/50 bg-background hover:bg-accent font-black text-[11px] uppercase tracking-widest shadow-lg shadow-black/5 transition-all active:scale-95'
                                      >
                                        <PenLine
                                          size={16}
                                          strokeWidth={2.5}
                                          className='mr-2'
                                        />
                                        Modify
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </motion.tr>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={isTeamLeader ? 8 : 9}
                          className='h-[400px] text-center'
                        >
                          <div className='flex flex-col items-center gap-6 text-muted-foreground/30'>
                            <div className='p-8 rounded-full bg-accent/10 border border-dashed border-border/50'>
                              <Layers size={64} strokeWidth={1} />
                            </div>
                            <div className='space-y-1'>
                              <p className='text-sm font-black uppercase tracking-[0.2em]'>
                                No Strategic Data
                              </p>
                              <p className='text-[10px] font-bold uppercase tracking-widest opacity-60'>
                                Please adjust filters or check another week
                              </p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <PlanDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        mode={dialog.mode}
        member={dialog.member}
        weekStart={weekStartStr}
        onAckRequired={handleAckRequiredFromPlanDialog}
      />

      {reviewMember && (
        <PreviousWeekReviewDialog
          isOpen={!!reviewMember}
          onClose={() => setReviewMember(null)}
          memberUserId={reviewMember.user_id}
          memberName={reviewMember.member_name}
          weekStart={weekStartStr}
          onAcknowledged={handleReviewAcknowledged}
        />
      )}
      <WeeklyViewModel
        isOpen={viewPlanId !== null}
        onClose={() => setViewPlanId(null)}
        planId={viewPlanId}
      />
    </div>
  )
}
